import { isFunctionValue } from "@sameday/core/typePredicates";

/**
 * Browser page-teardown and connectivity signals, for deciding whether a
 * response-less request failure is worth reporting.
 *
 * When the browser tears a document down — tab closed, reload, navigation away,
 * app switched on iOS — it kills every in-flight XHR at once. axios rejects each with a
 * bare `ERR_NETWORK` and no response, indistinguishable at the axios layer from
 * a real outage. That is the entire page-mount query set failing together, and
 * it is not a failure of anything. These predicates let the failure reporter
 * recognize it.
 *
 * Not exported from the `@/utils` barrel: `apiErrorReporting` is the only
 * consumer and imports it by leaf path, which keeps the barrel out of the entry
 * chunk (see that module's import comment).
 */

/**
 * How long after a teardown signal a response-less failure is still attributed
 * to that teardown.
 *
 * Teardown-induced rejections land within milliseconds of the event — the
 * browser kills the sockets synchronously — so this window is generous by two
 * orders of magnitude. It exists so a *missed clear* can never mute reporting
 * for more than a few seconds.
 *
 * That asymmetry is the whole design. Every event that can set the flag is a
 * teardown signal; every event that can clear it is proof of life. A missed set
 * costs a little noise. A missed clear would mute a live tab for the rest of
 * its session — so the flag expires on its own rather than trusting the clear
 * events to fire. `pageshow` and `visibilitychange` are still wired below, but
 * they are now belt-and-braces rather than load-bearing.
 */
const TEARDOWN_ATTRIBUTION_WINDOW_MS = 5_000;

/**
 * When the last teardown signal fired, or `null` if none has. Module-level
 * state paired with the listeners that write it — a second module instance
 * would give a writer and a reader that never meet.
 */
let teardownStartedAt: number | null = null;

const markTeardownStarted = (): void => {
  teardownStartedAt = Date.now();
};

const markPageAlive = (): void => {
  teardownStartedAt = null;
};

/**
 * True while a document teardown is plausibly in progress.
 *
 * `pagehide` is the only set signal:
 * - `beforeunload` is rejected because `useUnsavedChangesGuard` calls
 *   `preventDefault()` on it. If the user cancels that prompt the page stays
 *   live and `pagehide` never fires, so a `beforeunload`-set flag would only be
 *   cleared by the expiry below.
 * - `visibilitychange` → `hidden` is rejected because a backgrounded tab is
 *   alive and its request failures are real.
 */
export const isPageUnloading = (): boolean => {
  if (teardownStartedAt === null) return false;
  const elapsed = Date.now() - teardownStartedAt;
  // `Date.now()` is wall-clock, not monotonic. An NTP correction can step it
  // backwards — most likely right after the sleep/wake cycle that is itself a
  // teardown cause — which would make `elapsed` negative and satisfy a bare
  // upper-bound check forever, restoring the exact permanent-mute failure this
  // window exists to remove. A reading outside [0, window] is treated as "no
  // longer attributable", so an unusable clock fails toward reporting.
  //
  // A pure read: the flag is cleared only by the proof-of-life listeners, never
  // as a side effect of asking about it.
  return elapsed >= 0 && elapsed <= TEARDOWN_ATTRIBUTION_WINDOW_MS;
};

/**
 * True only when the browser positively reports no connectivity.
 *
 * `=== false` rather than `!navigator.onLine` on purpose: a runtime where the
 * property is absent (the DOM-less unit test project, a non-browser embed) reads
 * `undefined`, and an unknown connectivity state must still report. Suppression
 * requires the browser to actively say it is offline.
 *
 * Known limitation: `onLine === true` only means an interface has a route, so
 * captive portals and dead VPNs still read `true`. This removes a subset of
 * unreachable-API noise, not the whole class — the reporter's collapsed
 * `api-unreachable` grouping is what makes the remainder tolerable.
 */
export const isBrowserOffline = (): boolean =>
  globalThis.navigator !== undefined && navigator.onLine === false;

if (
  globalThis.window !== undefined &&
  isFunctionValue(window.addEventListener)
) {
  window.addEventListener("pagehide", markTeardownStarted);
  // Unconditional, NOT gated on `event.persisted`: a restore is proof of life
  // however the browser got there.
  window.addEventListener("pageshow", markPageAlive);
}

// `visibilitychange` is registered on its spec target rather than on `window`.
// It does bubble, so a window listener works in a browser — but only because
// the browser dispatches it as a bubbling event, which is an assumption about
// the dispatcher rather than about this module. Listening where the event is
// actually fired holds regardless.
if (
  globalThis.document !== undefined &&
  isFunctionValue(document.addEventListener)
) {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") markPageAlive();
  });
}
